<template>
    <div ref="elementRef" class="grid">
        <transition mode="out-in" tag="div"
                    enter-active-class="animated fadeIn u-anim-dur-350"
                    leave-active-class="animated fadeOut u-anim-dur-250">
            <div v-if="state === WebInquirySubscribeOverlayState.initial">
                <section>
                    <Form :submit="confirm">
                        <h1 class="h3 font-bold">
                            {{ computedProps.heading }}
                        </h1>

                        <p class="mt-20">
                            {{ computedProps.bodyText }}
                        </p>
                        <div v-show="computedProps.allowComment" class="mt-20">
                            <div class="font-bold">
                                {{ computedProps.commentInputHeader }}
                            </div>
                            <InputTextArea v-model="model.comment"
                                           name="comment"
                                           :input-classes="' '"
                                           maxlength="2000"
                                           :rows="5"
                                           :label="computedProps.commentInputPlaceholderText"/>
                        </div>
                        <div class="mb-5">
                            <InputCheckbox v-model="model.confirmTerm"
                                           name="terms"
                                           with-border
                                           :label="computedProps.contactConfirmationText || $dictionary.get('WebInquiry.TermAndConditionsText')"
                                           required/>
                        </div>
                        <div :class="{'mt-40': computedProps.allowComment, 'mt-15': !computedProps.allowComment }" class="flex items-center justify-between">
                            <CallToAction :disabled="pending"
                                          :text="computedProps.ctaText"
                                          :hex-color="computedProps.ctaColor"
                                          class="px-25"
                                          type="submit"/>
                        </div>
                        <WebApiMessage :id="apiMessageId"/>
                    </Form>
                </section>
            </div>

            <div v-else-if="state === WebInquirySubscribeOverlayState.success"
                 class="flex flex-col items-center content-center justify-center h-full pb-40">
                <h1 class="h3 font-bold">
                    {{ computedProps.successHeading }}
                </h1>
                <p class="mt-20">
                    {{ computedProps.successBodyText }}
                </p>
                <slot name="success"/>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { ref, PropType, computed, useTemplateRef } from 'vue';

import { SubmitWebInquiryRequest } from '@/types/apiServerContract';
import Api from '@/project/http/Api.service';
import WebApiMessage from '@/core/messages/WebApiMessage.vue';
import { useWebInquiryActions } from '@/project/app/blocks/webinquiries/useWebInquiryActions';
import GTMBasics from '@/project/tracking/gtm/GTM.basics';
import { WebInquirySubscribeOverlayOption } from '@/project/modals/overlays/WebInquirySubscribeOverlay.service';
import dictionaryService from '@/core/translation/dictionary.service';

enum WebInquirySubscribeOverlayState {
    initial,
    success
}

const { formatFields } = useWebInquiryActions();

export interface WebInquiryFormContent {
    showCommentInput: boolean;
    heading: string;
    bodyText: string;
    commentInputHeader: string;
    commentInputPlaceholderText: string;
    contactConfirmationText: string;
    ctaText: string;
    ctaColor: string;
    successHeading: string;
    successBodyText: string;
    instructions?: string;
    webInquiryOption: WebInquirySubscribeOverlayOption;
}

const props = defineProps({
    content: { type: Object as PropType<WebInquiryFormContent>, required: true },
});

const emit = defineEmits<{
    (event: 'submit', data: SubmitWebInquiryRequest): void;
}>();

const computedProps = computed(() => {
    const formattedFields = formatFields({
        header: props.content.heading,
        subheader: props.content.bodyText,
        brand: props.content?.webInquiryOption.brand,
        receiptHeader: props.content.successHeading || dictionaryService.get('WebInquiry.DefaultReceiptHeader'),
        receiptBodyText: props.content.successBodyText || dictionaryService.get('WebInquiry.DefaultReceiptBodyText'),
        commentInputHeader: props.content.commentInputHeader,
        commentInputPlaceholderText: props.content.commentInputPlaceholderText,
        contactConfirmationText: props.content?.contactConfirmationText,
        ctaText: props.content.ctaText || dictionaryService.get('Generic.Send'),
    });
    return {
        ...formattedFields,
        ctaColor: props.content.ctaColor,
        brand: props.content?.webInquiryOption.brand,
        isPrivateLabel: props.content.webInquiryOption.isPrivateLabel,
        businessLine: props.content.webInquiryOption.businessLines.join(','),
        allowComment: props.content.showCommentInput,
        webInquiryName: props.content.webInquiryOption.webInquiryName,
        instructions: props.content.instructions || '',
    };
});

const state = ref<WebInquirySubscribeOverlayState>(WebInquirySubscribeOverlayState.initial);

const apiMessageId = 'WebInquirySubscribeForm';
const model = ref({
    comment: '',
    confirmTerm: false
});
const elementRef = useTemplateRef<HTMLElement | null>('elementRef');
const pending = ref(false);

const confirm = async({ isValid }: { isValid: boolean }) => {
    if (pending.value || !isValid) { return; }

    const data: SubmitWebInquiryRequest = {
        brand: computedProps.value.brand || '',
        businessLine: computedProps.value.businessLine,
        comment: model.value.comment,
        webInquiryName: computedProps.value.webInquiryName,
        isPrivateLabel: computedProps.value.isPrivateLabel,
        triggeredAtUrl: window.location.href,
        instructions: computedProps.value.instructions
    };
    try {
        pending.value = true;
        const response = await Api.webInquiries.postWebInquiry(data, apiMessageId);

        // Keep height of the element to avoid page jumping when setting state
        if (elementRef.value) {
            const computedStyle = getComputedStyle(elementRef.value);
            if (computedStyle.height) {
                elementRef.value.style.minHeight = computedStyle.height;
            }
        }
        emit('submit', data);

        state.value = WebInquirySubscribeOverlayState.success;
        GTMBasics.pushToDataLayer({
            event: 'form.submit',
            submittedForm: 'inquiry'
        });
    } catch (error) {
    } finally {
        pending.value = false;
    }
};
</script>

